/**
 * Base API client with JWT access-token management, refresh-token handling,
 * and a uniform error abstraction.
 */

import type {
  ApiError,
  JwtClaims,
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
} from '@/types';

// ============================================================
// Constants
// ============================================================

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const ACCESS_TOKEN_KEY = 'roofer_access_token';
const REFRESH_TOKEN_KEY = 'roofer_refresh_token';

// ============================================================
// Token Storage
// ============================================================

export function setTokens(access: string, refresh: string): void {
  sessionStorage.setItem(ACCESS_TOKEN_KEY, access);
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
}

function clearTokens(): void {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

function getAccessToken(): string | null {
  return sessionStorage.getItem(ACCESS_TOKEN_KEY);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

// ============================================================
// JWT Decoding (no signature verification — server validates)
// ============================================================

export function decodeJwt(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JwtClaims;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const claims = decodeJwt(token);
  if (!claims) return true;
  // Treat tokens as expired 60 seconds before actual expiry (clock skew buffer)
  return Date.now() / 1000 > claims.exp - 60;
}

// ============================================================
// API Error
// ============================================================

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
    public readonly errors?: Record<string, string[]>,
  ) {
    super(detail);
    this.name = 'ApiClientError';
  }
}

// ============================================================
// Request Queue (for refresh-token serialisation)
// ============================================================

let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (err: unknown) => void;
}> = [];

async function processRefreshQueue(token: string | null, error: unknown): Promise<void> {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  refreshQueue = [];
}

// ============================================================
// Core Fetch Wrapper
// ============================================================

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  let accessToken = getAccessToken();

  // Proactively refresh if the token is expired
  if (accessToken && isTokenExpired(accessToken)) {
    accessToken = await refreshAccessToken();
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (response.status === 401) {
    // Token rejected by server — attempt refresh once, then log out
    try {
      const newToken = await refreshAccessToken();
      headers['Authorization'] = `Bearer ${newToken}`;
      const retryResponse = await fetch(`${BASE_URL}${path}`, {
        ...init,
        headers,
      });
      return parseResponse<T>(retryResponse);
    } catch {
      clearTokens();
      window.dispatchEvent(new CustomEvent('auth:logout'));
      throw new ApiClientError(401, 'Session expired. Please log in again.');
    }
  }

  return parseResponse<T>(response);
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  const contentType = response.headers.get('content-type') ?? '';

  if (!response.ok) {
    if (contentType.includes('application/json')) {
      const err = await response.json() as ApiError;
      throw new ApiClientError(
        response.status,
        err.detail ?? err.title ?? `HTTP ${response.status}`,
        err.errors,
      );
    }
    throw new ApiClientError(response.status, `HTTP ${response.status}`);
  }

  if (contentType.includes('application/json')) {
    return response.json() as Promise<T>;
  }

  // Binary response (export downloads etc.)
  return response.blob() as unknown as T;
}

// ============================================================
// Token Refresh
// ============================================================

async function refreshAccessToken(): Promise<string> {
  if (isRefreshing) {
    // Serialise concurrent refreshes
    return new Promise<string>((resolve, reject) => {
      refreshQueue.push({ resolve, reject });
    });
  }

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    throw new ApiClientError(401, 'No refresh token available.');
  }

  isRefreshing = true;

  try {
    const body: RefreshTokenRequest = { refreshToken };
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new ApiClientError(401, 'Token refresh failed.');
    }

    const data = await response.json() as RefreshTokenResponse;
    setTokens(data.accessToken, data.refreshToken);
    await processRefreshQueue(data.accessToken, null);
    return data.accessToken;
  } catch (err) {
    await processRefreshQueue(null, err);
    throw err;
  } finally {
    isRefreshing = false;
  }
}

// ============================================================
// Auth Helpers
// ============================================================

export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const response = await fetch(`${BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });

  const data = await parseResponse<LoginResponse>(response);
  setTokens(data.accessToken, data.refreshToken);
  return data;
}

export function logout(): void {
  clearTokens();
  window.dispatchEvent(new CustomEvent('auth:logout'));
}

export function getCurrentUser(): JwtClaims | null {
  const token = getAccessToken();
  if (!token) return null;
  if (isTokenExpired(token)) return null;
  return decodeJwt(token);
}

export function isAuthenticated(): boolean {
  return getCurrentUser() !== null;
}

// ============================================================
// HTTP Convenience Methods
// ============================================================

export const api = {
  get<T>(path: string, init?: RequestInit): Promise<T> {
    return apiFetch<T>(path, { ...init, method: 'GET' });
  },

  post<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return apiFetch<T>(path, {
      ...init,
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return apiFetch<T>(path, {
      ...init,
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  patch<T>(path: string, body?: unknown, init?: RequestInit): Promise<T> {
    return apiFetch<T>(path, {
      ...init,
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },

  delete<T = void>(path: string, init?: RequestInit): Promise<T> {
    return apiFetch<T>(path, { ...init, method: 'DELETE' });
  },
};
